import { useState } from "react";
import { AppHeader } from "./AppHeader";
import { BannerSupervisione } from "./BannerSupervisione";
import { DesktopHeader } from "./DesktopHeader";
import { DesktopNav, type DesktopNavItem } from "./DesktopNav";
import { MobileDrawer } from "./MobileDrawer";
import { TabBar } from "./TabBar";
import { tr } from "../../i18n";

/**
 * Layout responsive PWA/desktop.
 *
 * Mobile (< md, 768px):
 *  - AppHeader compatto (logo + bell + avatar)
 *  - Content max-w-md scrollable
 *  - TabBar fissa bottom con 5 sezioni
 *
 * Desktop (>= md):
 *  - DesktopHeader (wordmark + logo centrato + benvenuto + bell + avatar)
 *  - DesktopNav singola top: sinistra "squadra" + slash al centro + destra "strumenti"
 *  - Content max-w-7xl largo
 *  - NO TabBar bottom (nascosta da md:hidden)
 *  - "Gestione" non più nella nav: spostata negli shortcut della Dashboard.
 *
 * Lo stesso `children` viene renderizzato in entrambe le varianti; il layout
 * cambia solo via classi responsive Tailwind.
 */

// === Voci LEFT (consultazione lega) ===
const LEFT_NAV: DesktopNavItem[] = [
  {
    to: "/giornata",
    label: tr("Calendario"),
    match: (p) => p.startsWith("/giornata"),
  },
  {
    to: "/rose",
    label: tr("Rose"),
    match: (p) => p === "/rose",
  },
  {
    to: "/formazione",
    label: tr("Formazione"),
    match: (p) => p.startsWith("/formazione"),
  },
  {
    to: "/regole",
    label: tr("Regole"),
    match: (p) => p === "/regole",
  },
];

// === Voci RIGHT (strumenti della propria squadra) ===
const RIGHT_NAV: DesktopNavItem[] = [
  {
    // Gestione Rosa include /rinnovi (sub-area gestione)
    to: "/rosa",
    label: tr("Gestione Rosa"),
    match: (p) => p === "/rosa" || p.startsWith("/rinnovi"),
  },
  {
    to: "/budget",
    label: tr("Budget"),
    match: (p) => p.startsWith("/budget"),
  },
  {
    // Mercato include catalogo Giocatori (/giocatori)
    to: "/mercato",
    label: tr("Mercato"),
    match: (p) => p.startsWith("/mercato") || p.startsWith("/giocatori"),
  },
  {
    to: "/storico",
    label: tr("Storico"),
    match: (p) => p === "/storico",
  },
];

interface MobileShellProps {
  title?: string;
  logoSuffix?: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}

export function MobileShell({ title, logoSuffix, headerRight, children }: MobileShellProps) {
  // Andrea 2026-05-23: drawer mobile aperto via hamburger in AppHeader.
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-bg text-fg flex flex-col">
      {/* Admin app dentro una lega non sua (monitor leghe, 2026-10-06) */}
      <BannerSupervisione />

      {/* === Mobile header (< md) — con hamburger per aprire drawer === */}
      <div className="md:hidden">
        <AppHeader
          title={title}
          logoSuffix={logoSuffix}
          rightSlot={headerRight}
          onOpenDrawer={() => setDrawerOpen(true)}
        />
      </div>

      {/* === Desktop header + unica top nav (>= md) === */}
      <div className="hidden md:block">
        <DesktopHeader />
        <DesktopNav leftItems={LEFT_NAV} rightItems={RIGHT_NAV} />
      </div>

      {/* === Content area — responsive max-w === */}
      {/* Andrea 2026-10-16: lo spazio in fondo segue la barra: pb era 96px fissi,
          ma su iPhone la barra e' piu' alta (safe-area dei gesti, ~34px) e il
          bottone Home sporge di altri 26px, quindi copriva l'ultima riga. Ora
          barra (~68px) + bottone (26px) + respiro + safe-area del telefono. */}
      <main className="flex-1 w-full px-4 py-4 pb-[calc(7rem+env(safe-area-inset-bottom))] md:py-6 md:pb-6 mx-auto max-w-md md:max-w-7xl space-y-4">
        {children}
      </main>

      {/* === Mobile drawer (< md) — slide da sinistra === */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* === Mobile tab bar (< md, nascosta da md:hidden nel componente) === */}
      <TabBar />
    </div>
  );
}
